import { useApolloClient } from '@apollo/client';
import {
  Box,
  Dropdown,
  DropdownButton,
  DropdownMenu,
  DropdownMenuItem,
  Icon,
  IconProps,
  Pill,
  Popover,
  Stack,
  Text,
  Tooltip,
  useNotifications,
} from '@sixfold/common-ui';
import { BackofficeScope } from '@sixfold/session-interface';
import { isNil, notNil } from '@sixfold/typed-primitives';
import classnames from 'classnames';
import { differenceInMinutes, formatDistanceStrict, intlFormatDistance, subHours } from 'date-fns';
import { gql } from 'graphql-tag';
import React, { useState, type JSX } from 'react';
import { NavLink } from 'react-router';

import { Captcha } from './captcha';
import { ConfirmButton } from '../../components/confirm_button';
import { ScrollableMenu } from '../../components/data_to_list';
import { FormattedDate } from '../../components/date_formatting/formatted_date';
import { emptyTableState, getTableHeaderClassNames } from '../../components/table';
import { useHasScopes, useIsAdmin } from '../../lib/authorization';
import {
  IntegrationSyncResultIntegrationType,
  IntegrationSyncResultResultType,
  TelematicsIntegrationDiagnosticStatus,
} from '../../lib/graphql';
import { Table, SortBy } from '../../lib/table';
import { embedURLsInText } from '../../lib/util/text';
import { customSlugify, useUrlSort } from '../../lib/util/url';
import { Routes } from '../../routes';
import { IntegrationCredentialsList } from '../containers/integration_credentials_list_container';
import { IntegrationSyncHistory, TelematicIntegration, UserProfile } from '../entities';
import { updateTelematicsIntegrationSyncStateNextSyncAtMutation, sensitiveCliCommandDataQuery } from '../graphql';

const validateTelematicsIntegrationMutation = gql`
  mutation ValidateTelematicsIntegration($input: ValidateTelematicsIntegrationInput!) {
    validateTelematicsIntegration(input: $input) {
      areValidCredentials
    }
  }
`;

import styles from './styles.module.css';

export interface IntegrationListProps {
  integrations: TelematicIntegration[] | null;
  preRefreshProviderError?: boolean;
}

interface Props extends IntegrationListProps {
  deleteTelematicsIntegration?: (telematicsIntegrationId: string) => Promise<void>;
  hideCredentials?: boolean;
}

class IntegrationTable extends Table<TelematicIntegration> {}

export type SortableColumn = (typeof sortableColumns)[number];
const sortableColumns = [
  'telematics_integration_id',
  'telematics_provider_id',
  'company_id',
  'createdAt',
  'updatedAt',
] as const;

export const useIntegrationListSort = () =>
  useUrlSort<SortableColumn>({
    key: 'telematics_provider_id',
    direction: 'ASC',
  });

export const IntegrationList: React.FunctionComponent<Props> = ({
  integrations,
  hideCredentials = false,
  deleteTelematicsIntegration,
  preRefreshProviderError,
}) => {
  const isAdmin = useIsAdmin();
  const hasIntegrationsWritePermission = useHasScopes([BackofficeScope.telematicsWriteIntegrations]);
  const showForPrivilegedUser = isAdmin || hasIntegrationsWritePermission;

  const yesterday = subHours(new Date(), 24).toISOString();

  const { sort, changeSort } = useIntegrationListSort();

  const onSortByChange = (newSortBy: SortBy) => {
    if (sortableColumns.includes(newSortBy.keyPath as SortableColumn)) {
      changeSort({
        key: newSortBy.keyPath as SortableColumn,
        direction: newSortBy.value,
      });
    }
  };

  const integrationsTableHeaderColumns: { keyPath?: string; value: string | JSX.Element }[] = [
    {
      keyPath: 'telematics_integration_id',
      value: '#',
    },
    {
      keyPath: 'telematics_provider_id',
      value: 'Provider ID',
    },
    {
      keyPath: 'company_name',
      value: 'Company',
    },
    {
      keyPath: 'createdAt',
      value: 'Created at',
    },
    {
      keyPath: 'updatedAt',
      value: 'Updated at',
    },
    hideCredentials ? null : { value: 'Credentials' },
    {
      keyPath: 'enabled',
      value: 'Status',
    },
    {
      keyPath: 'syncHistory',
      value: 'Sync history',
    },
    showForPrivilegedUser ? { value: 'Debug' } : null,
    {
      keyPath: 'activeTrucksCount',
      value: (
        <span>
          <Tooltip placement="top" content="Trucks: active (signal age 2 weeks) / total">
            Trucks <Icon name="small-info" />
          </Tooltip>
        </span>
      ),
    },
    {
      keyPath: 'recentAllocationsCount',
      value: (
        <span>
          <Tooltip placement="top" content="Allocated trucks (last 2 weeks)">
            Allocations <Icon name="small-info" />
          </Tooltip>
        </span>
      ),
    },
    {
      keyPath: 'normalizedDiagnosticStatus',
      value: 'Diagnostic status',
    },
    showForPrivilegedUser && deleteTelematicsIntegration !== undefined ? { value: 'Delete' } : null,
  ].filter(notNil);

  const normalizedIntegrations =
    integrations !== null
      ? integrations.map((integration) => ({
          ...integration,
          normalizedDiagnosticStatus: integration?.diagnosticStatus ?? '',
        }))
      : [];

  return (
    <div className={styles.integrationList}>
      <IntegrationTable
        className="ui-table compact-table"
        tableHeaders={{
          defaultClassName: getTableHeaderClassNames,
          columns: integrationsTableHeaderColumns,
        }}
        data={normalizedIntegrations}
        sortBy={{
          keyPath: sort.key,
          value: sort.direction,
        }}
        onSortByChange={onSortByChange}
        emptyStatePlaceholder={emptyTableState('No integrations')}>
        {({ row }) => {
          const {
            company_id,
            company: { company_name },
            telematics_integration_id,
            telematics_provider_id,
            telematicsProvider,
            connection_data,
            enabled,
            diagnosticStatus,
            diagnosticComment,
            createdAt,
            updatedAt,
            isDeletable,
            createdBy,
            updatedBy,
            integrationApiDebugUrl,
            integrationApiDebugWithSyncStateUrl,
            integrationSyncHistory,
            totalTrucksCount,
            activeTrucksCount,
            recentAllocationsCount,
          } = row.data;

          const lastHistoricalResult: IntegrationSyncHistory | undefined = integrationSyncHistory
            ?.filter((res) => res.resultType === IntegrationSyncResultResultType.HISTORICAL_RESULT)
            .at(-1);
          const lastSuccess: IntegrationSyncHistory | undefined = integrationSyncHistory
            ?.filter((res) => isNil(res.errorType))
            .at(-1);
          const lastResult: IntegrationSyncHistory | undefined = integrationSyncHistory?.at(-1);

          const lastErrorDescription = lastResult?.errorDescription ?? undefined;

          const warnings = [
            notNil(lastResult?.vehicleAmbiguityCount) && lastResult?.vehicleAmbiguityCount > 0 ? (
              <span style={{ whiteSpace: 'nowrap' }}>
                Vehicles discarded from being ambiguous: {lastResult.vehicleAmbiguityCount}{' '}
                {((notNil(lastSuccess?.conflictingLicensePlates) && lastSuccess?.conflictingLicensePlates.length > 0) ||
                  (notNil(lastSuccess?.conflictingRemoteIds) && lastSuccess?.conflictingRemoteIds.length > 0)) &&
                  renderConflictsPopup(lastSuccess)}
              </span>
            ) : undefined,
            notNil(lastResult?.distinctVehicleStatusFutureCount) && lastResult?.distinctVehicleStatusFutureCount > 0
              ? `Vehicles in future (Status): ${lastResult.distinctVehicleStatusFutureCount}`
              : undefined,
            notNil(lastResult?.distinctVehicleTemperatureFutureCount) &&
            lastResult?.distinctVehicleTemperatureFutureCount > 0
              ? `Vehicles in future (Temperature): ${lastResult.distinctVehicleTemperatureFutureCount}`
              : undefined,
            notNil(lastResult?.distinctVehicleNullIslandCount) && lastResult?.distinctVehicleNullIslandCount > 0
              ? `Null island coordinates: ${lastResult.distinctVehicleNullIslandCount}`
              : undefined,
          ].filter(notNil);

          const lastWarningDescription =
            warnings.length > 0
              ? warnings.length === 1
                ? warnings[0]
                : warnings.map((warning, index) => (
                    <React.Fragment key={index}>
                      {index === 0 ? <br /> : null}
                      <span style={{ whiteSpace: 'nowrap' }}>• {warning}</span>
                      {index < warnings.length - 1 ? <br /> : null}
                    </React.Fragment>
                  ))
              : undefined;

          return (
            <tr key={telematics_integration_id}>
              <td>
                <NavLink
                  to={Routes.CompanyTelematicsIntegration.generatePath({
                    company_id,
                    telematics_integration_id,
                    slug_company_name: customSlugify(company_name),
                  })}>
                  {telematics_integration_id}
                </NavLink>
              </td>
              <td>
                <NavLink to={Routes.TelematicsProvider.generatePath({ provider_id: telematics_provider_id })}>
                  {telematics_provider_id}
                </NavLink>
              </td>
              <td>
                <NavLink
                  to={Routes.CompanyTelematicsIntegrations.generatePath({
                    company_id,
                    slug_company_name: customSlugify(company_name),
                  })}>
                  {company_name ?? company_id}
                </NavLink>
              </td>
              <td>
                <div>
                  <FormattedDate date={createdAt} />
                </div>
                {userLink(createdBy)}
              </td>
              <td>
                <div>
                  <FormattedDate date={updatedAt} />
                </div>
                {userLink(updatedBy)}
              </td>
              {!hideCredentials && (
                <td>
                  {telematicsProvider !== null && telematicsProvider.connectionParameters !== null ? (
                    <IntegrationCredentialsList
                      integrationId={telematics_integration_id}
                      connectionData={connection_data ?? {}}
                      connectionParameters={telematicsProvider.connectionParameters}></IntegrationCredentialsList>
                  ) : null}
                </td>
              )}
              <td>{getEnabledFlag(enabled, telematicsProvider?.isDeprecated ?? false)}</td>
              <td>
                <div className={styles.syncHistory}>
                  {integrationSyncHistory?.map((syncHistory, idx) => {
                    return (
                      <Tooltip
                        key={idx}
                        placement="top"
                        content={
                          <Stack direction="column" style={{ minWidth: '300px' }}>
                            <Box style={{ textAlign: 'center' }}>
                              <p>
                                <strong className="bold-text">When: </strong>
                                {formatSecondsDuration(syncHistory.startTimeAt, new Date().toISOString())}
                              </p>
                              <p>
                                <strong className="bold-text">StartTime: </strong>
                                {syncHistory.startTimeAt}
                              </p>
                              <p>
                                <strong className="bold-text">EndTime: </strong>
                                {syncHistory.endTimeAt}
                              </p>
                              <p>
                                <strong className="bold-text">Duration:</strong>{' '}
                                {formatDistanceStrict(
                                  new Date(syncHistory.endTimeAt),
                                  new Date(syncHistory.startTimeAt),
                                )}
                              </p>
                              <br />
                              {syncHistory.integrationType ===
                              IntegrationSyncResultIntegrationType.CARRIER_INTEGRATION ? (
                                <>
                                  {syncHistory.tourPositionsCount !== null && (
                                    <p>
                                      <strong className="bold-text">Tour positions: </strong>
                                      {syncHistory.tourPositionsCount}
                                    </p>
                                  )}
                                  {syncHistory.stopEventsCount !== null && (
                                    <p>
                                      <strong className="bold-text">Stop events: </strong>
                                      {syncHistory.stopEventsCount}
                                    </p>
                                  )}
                                  {syncHistory.tourEventsCount !== null && (
                                    <p>
                                      <strong className="bold-text">Tour events: </strong>
                                      {syncHistory.tourEventsCount}
                                    </p>
                                  )}
                                  {syncHistory.tourAllocationsCount !== null && (
                                    <p>
                                      <strong className="bold-text">Tour allocations: </strong>
                                      {syncHistory.tourAllocationsCount}
                                    </p>
                                  )}
                                </>
                              ) : (
                                <>
                                  {syncHistory.vehicleCount !== null && (
                                    <p>
                                      <strong className="bold-text">Vehicles: </strong>
                                      {syncHistory.vehicleCount}
                                    </p>
                                  )}
                                  {syncHistory.statusCount !== null && (
                                    <p>
                                      <strong className="bold-text">Statuses: </strong>
                                      {syncHistory.statusCount}
                                    </p>
                                  )}
                                  {syncHistory.temperatureCount !== null && (
                                    <p>
                                      <strong className="bold-text">Temperatures: </strong>
                                      {syncHistory.temperatureCount}
                                    </p>
                                  )}
                                  {syncHistory.distinctVehicleStatusFutureCount !== null &&
                                    syncHistory.distinctVehicleStatusFutureCount > 0 && (
                                      <p>
                                        <strong className="bold-text">Vehicles in future (Status): </strong>
                                        {syncHistory.distinctVehicleStatusFutureCount}
                                      </p>
                                    )}
                                  {syncHistory.distinctVehicleTemperatureFutureCount !== null &&
                                    syncHistory.distinctVehicleTemperatureFutureCount > 0 && (
                                      <p>
                                        <strong className="bold-text">Vehicles in future (Temperature): </strong>
                                        {syncHistory.distinctVehicleTemperatureFutureCount}
                                      </p>
                                    )}
                                  {syncHistory.distinctVehicleNullIslandCount !== null &&
                                    syncHistory.distinctVehicleNullIslandCount > 0 && (
                                      <p>
                                        <strong className="bold-text">Null island coordinates: </strong>
                                        {syncHistory.distinctVehicleNullIslandCount}
                                      </p>
                                    )}
                                  {syncHistory.vehicleAmbiguityCount !== null &&
                                    syncHistory.vehicleAmbiguityCount > 0 && (
                                      <p>
                                        Vehicles discarded from being ambiguous: {syncHistory.vehicleAmbiguityCount}
                                      </p>
                                    )}
                                  {syncHistory.latestStatusTimestamp !== null && (
                                    <p>
                                      <strong className="bold-text">Latest status freshness:</strong>{' '}
                                      {formatSecondsDuration(syncHistory.latestStatusTimestamp, syncHistory.endTimeAt)}
                                    </p>
                                  )}
                                  {syncHistory.latestTemperatureTimestamp !== null && (
                                    <p>
                                      <strong className="bold-text">Latest temperature freshness:</strong>{' '}
                                      {formatSecondsDuration(
                                        syncHistory.latestTemperatureTimestamp,
                                        syncHistory.endTimeAt,
                                      )}
                                    </p>
                                  )}
                                </>
                              )}
                            </Box>
                            <Box>
                              {(syncHistory.errorType !== null || syncHistory.errorDescription !== null) && (
                                <Text weight="bold" style={{ color: 'var(--color-danger-text)' }}>
                                  ERROR
                                </Text>
                              )}
                              {syncHistory.errorType !== null && (
                                <p>
                                  <strong className="bold-text">Type: </strong>
                                  {syncHistory.errorType}
                                </p>
                              )}
                              {syncHistory.errorDescription !== null && (
                                <p className={styles.syncHistoryErrorDescription}>
                                  <strong className="bold-text">Description: </strong>
                                  {syncHistory.errorDescription}
                                </p>
                              )}
                            </Box>
                          </Stack>
                        }>
                        <div className={styles.syncHistoryResult}>
                          {(() => {
                            const { kind, icon } = getSyncStatusStyle(syncHistory);
                            return (
                              <div className={styles.syncHistoryBox} data-status={kind}>
                                <Icon name={icon} size={14} color="currentColor" />
                              </div>
                            );
                          })()}
                          {syncHistory.startTimeAt === lastHistoricalResult?.startTimeAt && (
                            <span className={styles.syncHistoryResultHistoryflag}>»</span>
                          )}
                        </div>
                      </Tooltip>
                    );
                  })}
                </div>
                {notNil(lastSuccess) &&
                  (lastSuccess.resultType === IntegrationSyncResultResultType.HISTORICAL_RESULT ||
                    (enabled && lastSuccess.startTimeAt < yesterday)) && (
                    <div>
                      <br />
                      <p>
                        <Text weight="bold">Last success:</Text>{' '}
                        {formatSecondsDuration(lastSuccess.startTimeAt, new Date().toISOString())}
                      </p>
                    </div>
                  )}
                {notNil(lastErrorDescription) && (
                  <div>
                    {(isNil(lastSuccess) ||
                      lastSuccess.resultType !== IntegrationSyncResultResultType.HISTORICAL_RESULT) && <br />}
                    <p className={styles.syncHistoryLastError}>
                      <Text weight="bold">Last error:</Text> {lastErrorDescription}
                    </p>
                  </div>
                )}
                {notNil(lastWarningDescription) && (
                  <div>
                    <p className={styles.syncHistoryLastWarning}>
                      <Text weight="bold">Last warning{warnings.length > 1 ? 's' : ''}:</Text> {lastWarningDescription}
                      {((isNil(lastSuccess) && isNil(lastErrorDescription)) ||
                        lastSuccess?.resultType !== IntegrationSyncResultResultType.HISTORICAL_RESULT) && <br />}
                    </p>
                  </div>
                )}
              </td>
              {showForPrivilegedUser && (
                <td>
                  <DebugDropdown
                    integrationApiDebugWithSyncStateUrl={integrationApiDebugWithSyncStateUrl}
                    integrationApiDebugUrl={integrationApiDebugUrl}
                    telematics_integration_id={telematics_integration_id}
                    telematics_provider_id={telematics_provider_id}
                    connection_data={connection_data}
                  />
                </td>
              )}
              <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                {(activeTrucksCount !== -1 || totalTrucksCount !== -1) &&
                  !(activeTrucksCount === 0 && totalTrucksCount === 0) && (
                    <>
                      {activeTrucksCount !== -1 ? activeTrucksCount : '—'} /{' '}
                      {totalTrucksCount !== -1 ? totalTrucksCount : '—'}
                    </>
                  )}
              </td>
              <td style={{ textAlign: 'center' }}>{recentAllocationsCount > 0 ? recentAllocationsCount : ''}</td>
              <td>
                <div style={{ maxWidth: '200px' }}>
                  {diagnosticStatus !== null || preRefreshProviderError ? (
                    <Pill
                      kind={
                        diagnosticStatus === TelematicsIntegrationDiagnosticStatus.INTERMITTENT ? 'warning' : 'danger'
                      }>
                      {diagnosticStatus !== null
                        ? diagnosticStatus
                        : preRefreshProviderError
                          ? TelematicsIntegrationDiagnosticStatus.PROVIDER_ERROR
                          : ''}
                    </Pill>
                  ) : (
                    ''
                  )}
                  <ScrollableMenu data={embedURLsInText(diagnosticComment ?? '')} />
                </div>
              </td>
              {showForPrivilegedUser && deleteTelematicsIntegration !== undefined && (
                <td>
                  <ConfirmButton
                    size="tiny"
                    initialButtonColor="red"
                    label="Delete"
                    disabled={!isDeletable}
                    onConfirm={async () => {
                      await deleteTelematicsIntegration(telematics_integration_id);
                    }}
                  />
                </td>
              )}
            </tr>
          );
        }}
      </IntegrationTable>
    </div>
  );
};

interface DebugDropdownProps {
  integrationApiDebugWithSyncStateUrl: string;
  integrationApiDebugUrl: string;
  telematics_integration_id: string;
  telematics_provider_id: string;
  connection_data: Record<string, unknown> | null | undefined;
}

const DebugDropdown: React.FC<DebugDropdownProps> = ({
  integrationApiDebugWithSyncStateUrl,
  integrationApiDebugUrl,
  telematics_integration_id,
  telematics_provider_id,
  connection_data,
}) => {
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [areValidCredentials, setAreValidCredentials] = useState<boolean | null>(null);
  const notify = useNotifications();
  const client = useApolloClient();

  const handleForceRun = async () => {
    try {
      await client.mutate({
        mutation: updateTelematicsIntegrationSyncStateNextSyncAtMutation,
        variables: {
          telematics_integration_id,
        },
      });
      notify.success({ title: 'Reset next sync time successful' });
    } catch (error) {
      console.error('Failed to force sync:', error);
      notify.error({
        title: 'Failed to reset next sync time',
      });
    }
  };

  const handleCopyCliCommand = async (captcha_token: string) => {
    try {
      const { data } = await client.query({
        query: sensitiveCliCommandDataQuery,
        variables: {
          telematics_integration_id,
          captcha_token,
        },
      });

      if (data.sensitiveCliCommandData) {
        await navigator.clipboard.writeText(data.sensitiveCliCommandData);
        notify.success({ title: 'CLI command copied to clipboard' });
      } else {
        notify.error({ title: 'No CLI command available. This feature is only for pull-based integrations.' });
      }
    } catch (error) {
      console.error('Failed to copy CLI command:', error);
      notify.error({
        title: 'Failed to copy CLI command',
      });
    }
    setShowCaptcha(false);
  };

  const handleValidateCredentials = async (): Promise<false> => {
    try {
      const { data } = await client.mutate({
        mutation: validateTelematicsIntegrationMutation,
        variables: {
          input: {
            telematics_provider_id,
            telematics_integration_id,
          },
        },
      });

      const isValid = data?.validateTelematicsIntegration?.areValidCredentials;
      setAreValidCredentials(isValid ?? false);

      if (isValid) {
        notify.success({ title: 'Credentials are valid' });
      } else {
        notify.error({ title: 'Credentials are invalid' });
      }
    } catch (error) {
      console.error('Failed to validate credentials:', error);
      setAreValidCredentials(false);
      notify.error({
        title: 'Failed to validate credentials',
      });
    }
    return false;
  };

  return (
    <>
      {showCaptcha && (
        <Captcha
          onClickAway={() => setShowCaptcha(false)}
          onChange={async (captcha_token) => {
            if (captcha_token) {
              await handleCopyCliCommand(captcha_token);
            }
          }}
        />
      )}
      <Dropdown align="start">
        <DropdownButton kind="primary" size="small" iconStart="small-hammer" iconEnd="small-chevron-down">
          <Text>Debug</Text>
        </DropdownButton>
        <DropdownMenu size="small">
          <DropdownMenuItem
            onClick={() => {
              window.open(integrationApiDebugWithSyncStateUrl, '_blank', 'noopener,noreferrer');
            }}>
            Debug with SyncState
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => {
              window.open(integrationApiDebugUrl, '_blank', 'noopener,noreferrer');
            }}>
            Debug without SyncState
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleForceRun}>Reset next sync time</DropdownMenuItem>
          <DropdownMenuItem onClick={() => setShowCaptcha(true)}>Copy CLI command</DropdownMenuItem>
          {notNil(connection_data) && (
            <DropdownMenuItem
              onClick={handleValidateCredentials}
              className={classnames({
                [styles.debugButtonMenuItemValid]: areValidCredentials === true,
                [styles.debugButtonMenuItemInvalid]: areValidCredentials === false,
              })}>
              {areValidCredentials === null
                ? 'Validate credentials'
                : areValidCredentials
                  ? '✓ Valid credentials'
                  : '✗ Invalid credentials'}
            </DropdownMenuItem>
          )}
        </DropdownMenu>
      </Dropdown>
    </>
  );
};

type SyncStatusKind = 'success' | 'success-partial' | 'warning' | 'error' | 'error-auth' | 'nodata';

function getSyncStatusStyle(result: IntegrationSyncHistory): { kind: SyncStatusKind; icon: IconProps['name'] } {
  const { errorType } = result;

  if (errorType !== null) {
    if (errorType === 'UNAUTHORIZED') {
      return { kind: 'error-auth', icon: 'small-lock' };
    }
    return { kind: 'error', icon: 'small-cross' };
  }

  if (result.integrationType === IntegrationSyncResultIntegrationType.CARRIER_INTEGRATION) {
    return getCarrierSyncStatusStyle(result);
  }

  return getFmsSyncStatusStyle(result);
}

function getCarrierSyncStatusStyle(result: IntegrationSyncHistory): { kind: SyncStatusKind; icon: IconProps['name'] } {
  const hasData = [
    result.tourPositionsCount,
    result.stopEventsCount,
    result.tourEventsCount,
    result.tourAllocationsCount,
  ].some((count) => count !== null && count > 0);

  if (!hasData) {
    return { kind: 'nodata', icon: 'small-minus' };
  }

  return { kind: 'success', icon: 'small-check' };
}

function getFmsSyncStatusStyle(result: IntegrationSyncHistory): { kind: SyncStatusKind; icon: IconProps['name'] } {
  const {
    vehicleCount,
    temperatureCount,
    statusCount,
    distinctVehicleStatusFutureCount,
    latestStatusTimestamp,
    latestTemperatureTimestamp,
  } = result;

  let typeCount = 3;
  if (vehicleCount === null || vehicleCount === 0) {
    typeCount -= 1;
  }
  if (statusCount === null || statusCount === 0) {
    typeCount -= 1;
  }
  if (temperatureCount === null || temperatureCount === 0) {
    typeCount -= 1;
  }

  if (typeCount === 0) {
    return { kind: 'nodata', icon: 'small-minus' };
  }

  const endTimeAt = new Date(result.endTimeAt);
  const statusesStale =
    latestStatusTimestamp !== null && differenceInMinutes(endTimeAt, new Date(latestStatusTimestamp)) > 30;
  const temperaturesStale =
    latestTemperatureTimestamp !== null && differenceInMinutes(endTimeAt, new Date(latestTemperatureTimestamp)) > 30;
  const hasFutureTimestamps = distinctVehicleStatusFutureCount !== null && distinctVehicleStatusFutureCount > 0;

  if (statusesStale || temperaturesStale || hasFutureTimestamps) {
    return { kind: 'warning', icon: 'small-alert' };
  }

  if (typeCount < 3) {
    return { kind: 'success-partial', icon: 'small-check' };
  }

  return { kind: 'success', icon: 'small-check' };
}

function formatSecondsDuration(a: string | number, b: string | number): string {
  return intlFormatDistance(new Date(a), new Date(b));
}

function getEnabledFlag(enabled: boolean, isDeprecated: boolean) {
  const text = enabled ? 'Enabled' : 'Disabled';
  const kind = isDeprecated ? 'neutral' : enabled ? 'success' : 'danger';
  return <Pill kind={kind}>{text}</Pill>;
}

function userLink(user: UserProfile | null) {
  if (isNil(user)) {
    return null;
  }

  if (isNil(user.first_name) && isNil(user.last_name)) {
    return null;
  }

  return (
    <div>
      {' by '}
      <NavLink
        to={Routes.User.generatePath({
          user_id: user.user_id,
        })}>{`${user.first_name ?? ''} ${user.last_name ?? ''}`}</NavLink>
    </div>
  );
}

function renderConflictsPopup(lastSuccess: IntegrationSyncHistory | undefined) {
  if (!lastSuccess) return null;

  const licensePlateConflicts = lastSuccess.conflictingLicensePlates;
  const remoteIdConflicts = lastSuccess.conflictingRemoteIds;

  if (!licensePlateConflicts?.length && !remoteIdConflicts?.length) {
    return null;
  }

  return (
    <Popover
      placement="top"
      content={
        <div style={{ minWidth: '500px' }}>
          {notNil(licensePlateConflicts) && licensePlateConflicts?.length > 0 && (
            <div>
              <strong>Conflicting license plates (max 10 displayed):</strong>
              <ul>
                {licensePlateConflicts.map((plate) => (
                  <li key={plate.licensePlate}>
                    {plate.licensePlate} - Remote id-s: [{plate.remoteIds?.join(', ')}]
                  </li>
                ))}
              </ul>
            </div>
          )}
          {notNil(remoteIdConflicts) && remoteIdConflicts?.length > 0 && (
            <div>
              <strong>Conflicting remote id-s (max 10 displayed):</strong>
              <ul>
                {remoteIdConflicts.map((remote) => (
                  <li key={remote.remoteId}>
                    {remote.remoteId} - License plates: [{remote.licensePlates?.join(', ')}]
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      }>
      <Icon name="small-help" kind="info" />
    </Popover>
  );
}
